<script setup lang="ts">
import { getLocalTypeIconSrc, fallbackTypeIcon } from "../ui/typeIcon";
import { Tooltip } from "floating-vue";
import SkillInfo from "./SkillInfo.vue";
import { _battle } from "../ui/battle";
import { Seer } from "../core/seer";
import { Side } from "../core/round";
import { computed } from "vue";

const battle = _battle;
const props = defineProps<{ seer: Seer; side: Side }>();

const disabled = computed(
    () => battle.phase !== "selecting" || battle.viewMode !== "live"
);

/** 本方是否需要先补位（上回合出战精灵阵亡） */
const needReplacement = computed(() => battle.needsReplacement(props.side));
/** 本方已选择的补位精灵 */
const replacement = computed(() => battle.getReplacement(props.side));

/** 技能列表展示的精灵：补位方选定替补后显示替补精灵，否则显示出战精灵 */
const displayPet = computed(() => {
    if (needReplacement.value && replacement.value) {
        return replacement.value;
    }
    return props.seer.getBattlePet();
});

/** 是否阻止选择技能：需补位方在选定替补前不可操作 */
const actionBlocked = computed(() => {
    if (needReplacement.value) return !replacement.value;
    const p = props.seer.getBattlePet();
    return !p || !p.isAlive();
});

function isPending(skillIndex: number) {
    const op = battle.getPending(props.side);
    return op?.kind === "skill" && op.petId === displayPet.value?.序号 && op.skillIndex === skillIndex;
}

function ppLevel(cur: number, max: number): string {
    const ratio = max > 0 ? cur / max : 0;
    if (ratio > 0.5) return "pp-high";
    if (ratio > 0.25) return "pp-medium";
    return "pp-low";
}
</script>

<template>
    <div class="skill-group">
        <Tooltip
            class="skill-wrap"
            v-for="(skill, skillIndex) in displayPet?.技能组 ?? []"
            :key="skill.name"
        >
            <button
                @click="battle.selectOperation(side, { kind: 'skill', petId: displayPet!.序号, skillIndex })"
                class="skill-btn"
                :class="{
                    selected: isPending(skillIndex),
                    depleted: skill.curPP <= 0,
                }"
                :disabled="disabled || skill.curPP <= 0 || actionBlocked"
            >
                <img class="skill-type-icon" :src="getLocalTypeIconSrc(skill.type.name)" :data-fallback-src="skill.getSkillTypeUrl()" @error="fallbackTypeIcon" />
                <span class="skill-name">{{ skill.name }}</span>
                <!-- PP 电量条：按钮内部下方 -->
                <div
                    class="pp-bars"
                    :class="ppLevel(skill.curPP, skill.maxPP)"
                >
                    <span
                        v-for="i in skill.maxPP"
                        :key="i"
                        class="pp-cell"
                        :class="{ filled: i <= skill.curPP }"
                    ></span>
                </div>
            </button>

            <template #popper>
                <SkillInfo :skill="skill" />
            </template>
        </Tooltip>
    </div>
</template>

<style scoped>
.skill-group {
    display: inline-flex;
    gap: calc(var(--ui-unit) * 8);
}

.skill-wrap {
    display: flex;
}

/* ===== 技能按钮 ===== */
.skill-btn {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: calc(var(--ui-unit) * 5);
    width: calc(var(--ui-unit) * 82);
    /* height: calc(var(--ui-unit) * 90); */
    padding: calc(var(--ui-unit) * 6) calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 6) calc(var(--ui-unit) * 4);
    background: rgba(30, 30, 45, 0.9);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.08);
    border-radius: calc(var(--ui-unit) * 8);
    cursor: pointer;
    transition: all 0.2s ease;
    backdrop-filter: blur(calc(var(--ui-unit) * 6));
}

.skill-btn:hover:not(:disabled) {
    background: rgba(40, 40, 60, 0.95);
    border-color: rgba(255, 215, 0, 0.35);
    transform: translateY(calc(var(--ui-unit) * -2));
    box-shadow: 0 calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.12);
}

.skill-btn.selected {
    border-color: #ffd700;
    background: rgba(255, 215, 0, 0.08);
    box-shadow: 0 0 calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.2), inset 0 0 calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.04);
    transform: translateY(calc(var(--ui-unit) * -2));
}

.skill-btn:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    transform: none;
}

.skill-btn.depleted {
    opacity: 0.35;
}

.skill-type-icon {
    width: calc(var(--ui-unit) * 22);
    height: calc(var(--ui-unit) * 22);
    margin-top: calc(var(--ui-unit) * 2);
    filter: drop-shadow(0 0 calc(var(--ui-unit) * 4) rgba(255, 255, 255, 0.15));
}

.skill-name {
    color: #e0e0e0;
    font-size: calc(var(--ui-unit) * 12);
    font-weight: 600;
    line-height: 1.1;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

/* ===== PP 电量条（按钮内部下方，等宽分段） ===== */
.pp-bars {
    display: flex;
    width: 100%;
    height: calc(var(--ui-unit) * 4);
    gap: calc(var(--ui-unit) * 1);
    margin-top: calc(var(--ui-unit) * 2);
}

.pp-cell {
    flex: 1 1 0;
    min-width: 0;
    height: 100%;
    border-radius: calc(var(--ui-unit) * 1);
    background: rgba(255, 255, 255, 0.14);
    transition: background 0.3s ease, box-shadow 0.3s ease;
}

.pp-bars.pp-high .pp-cell.filled {
    background: #22c55e;
    box-shadow: 0 0 calc(var(--ui-unit) * 3) #22c55e;
}

.pp-bars.pp-medium .pp-cell.filled {
    background: #eab308;
    box-shadow: 0 0 calc(var(--ui-unit) * 3) #eab308;
}

.pp-bars.pp-low .pp-cell.filled {
    background: #ef4444;
    box-shadow: 0 0 calc(var(--ui-unit) * 3) #ef4444;
}
</style>
